import { create } from "zustand";

import { api } from "../api/rest";
import type { Trade } from "../api/types";
import { createBatcher } from "../lib/batch";
import { mergeTrade, tradeKey } from "../lib/trades";

/**
 * НАШИ сделки по рынку — источник мгновенной позиции.
 *
 * # Зачем отдельный стор, если есть useTape
 *
 * Затем, что лента — скользящее окно, а позиция — итог. Стор ленты держит тысячу
 * последних сделок НА ВСЮ ВКЛАДКУ по всем рынкам (useTape.TAPE_LIMIT); на активном
 * рынке этот буфер вытесняется за минуты, и наш филл, случившийся в начале окна, из
 * него уходит. Позиция, посчитанная по такому буферу, молча уменьшалась бы сама.
 *
 * Здесь же держатся ТОЛЬКО наши сделки, только по открытым рынкам, и не
 * вытесняются: их единицы за окно, а не тысячи.
 *
 * # Почему карта, а не список
 *
 * Одна сделка приезжает несколько раз, шагая по статусам MATCHED -> MINED ->
 * CONFIRMED под одним id. Карта делает повтор ЗАМЕЩЕНИЕМ — тем же приёмом, что в
 * useTape, и по той же причине: сорвавшаяся on-chain сделка обязана перестать
 * считаться, а не остаться в позиции навсегда.
 *
 * # Гидрация
 *
 * Из `GET /trades?market=…` — журнала НАШИХ сделок в Postgres. Без неё перезагрузка
 * вкладки посреди окна обнуляла бы позицию до следующего филла, при том что полчаса
 * назад по рынку прошли исполнения. Тот же приём, что у useOrders (status=all), и
 * та же причина.
 */

/** Сколько сделок тянем при гидрации рынка. */
const HYDRATE_LIMIT = 200;

interface FillsState {
  /** condition_id -> (tradeKey -> сделка): у двух своих одной сделки — по записи. */
  byMarket: Record<string, Record<string, Trade>>;
  /** Рынки, по которым гидрация уже прошла: второй раз ходить незачем. */
  hydrated: Record<string, boolean>;
  push: (t: Trade) => void;
  hydrate: (conditionID: string) => Promise<void>;
  /** Забыть рынок: вкладка ушла с него, и держать его сделки не нужно. */
  forget: (conditionID: string) => void;
  clear: () => void;
}

/**
 * Очередь на кадр, как у ленты: при частичных исполнениях филлы приходят пачкой,
 * и перерисовывать строку позиции на каждый — лишняя работа. Задержка кадра (16 мс)
 * на порядки меньше того отставания индексатора, ради которого стор и заведён.
 */
let pending: Trade[] = [];

const batcher = createBatcher(() => {
  if (pending.length === 0) return;
  const batch = pending;
  pending = [];
  useFills.setState((state) => {
    const byMarket = { ...state.byMarket };
    for (const t of batch) {
      if (!t.ours || !t.condition_id || !t.id) continue;
      const market = { ...(byMarket[t.condition_id] ?? {}) };
      // Замещение по id, но БЕЗ потери адреса агрессора: он приезжает отдельной
      // публикацией между шагами статуса, и следующий шаг стёр бы его (см.
      // lib/trades.mergeTrade).
      const key = tradeKey(t);
      market[key] = mergeTrade(market[key], t);
      byMarket[t.condition_id] = market;
    }
    return { byMarket };
  });
});

export const useFills = create<FillsState>((set, get) => ({
  byMarket: {},
  hydrated: {},

  push: (t) => {
    // Чужие сделки отбрасываются здесь: публичный принт не несёт ни our_size, ни
    // адреса, и сложить его с нашими значило бы приписать себе оборот рынка.
    if (!t.ours) return;
    pending.push(t);
    batcher.schedule();
  },

  hydrate: async (conditionID) => {
    if (!conditionID || get().hydrated[conditionID]) return;
    // Отметка ставится ДО запроса: страница рынка монтирует несколько
    // потребителей, и без неё каждый сходил бы за одной и той же историей.
    set((state) => ({ hydrated: { ...state.hydrated, [conditionID]: true } }));
    try {
      const res = await api.trades({ market: conditionID, limit: HYDRATE_LIMIT });
      const rows: Record<string, Trade> = {};
      for (const t of res.trades) {
        if (t.ours && t.id) rows[tradeKey(t)] = t;
      }
      set((state) => {
        // Живые филлы, доехавшие ПОКА шёл запрос, важнее гидрации: у них тот же
        // id и более поздний статус, и затирать их ответом REST значило бы
        // откатывать сделку из CONFIRMED в MATCHED.
        //
        // Но не важнее адреса агрессора: его знает как раз журнал, а живой шаг
        // статуса приезжает без него (см. lib/trades.mergeTrade).
        const merged = { ...rows };
        for (const [key, live] of Object.entries(state.byMarket[conditionID] ?? {})) {
          merged[key] = mergeTrade(rows[key], live);
        }
        return { byMarket: { ...state.byMarket, [conditionID]: merged } };
      });
    } catch {
      // Отказ гидрации не ломает счёт: позиция соберётся из живых филлов, а
      // снимок индексатора рядом остаётся вторым мнением. Снимаем отметку, чтобы
      // следующая попытка была возможна.
      set((state) => ({ hydrated: { ...state.hydrated, [conditionID]: false } }));
    }
  },

  forget: (conditionID) => {
    set((state) => {
      if (!state.byMarket[conditionID] && !state.hydrated[conditionID]) return state;
      const byMarket = { ...state.byMarket };
      const hydrated = { ...state.hydrated };
      delete byMarket[conditionID];
      delete hydrated[conditionID];
      return { byMarket, hydrated };
    });
  },

  clear: () => {
    pending = [];
    set({ byMarket: {}, hydrated: {} });
  },
}));

/**
 * Все наши сделки, какие есть в сторе.
 *
 * Для дашборда: там панель ордеров показывает все рынки сразу, и спрашивать
 * стор по одному рынку не по чему — рынок у каждой строки свой.
 */
export function allFills(byMarket: Record<string, Record<string, Trade>>): Trade[] {
  const out: Trade[] = [];
  for (const rows of Object.values(byMarket)) out.push(...Object.values(rows));
  return out;
}

/** Наши сделки по рынку списком. Пустой массив, если их нет. */
export function fillsOfMarket(
  byMarket: Record<string, Record<string, Trade>>,
  conditionID: string | undefined,
): Trade[] {
  if (!conditionID) return [];
  const rows = byMarket[conditionID];
  return rows ? Object.values(rows) : [];
}
